import React from 'react';
import { useInView } from 'react-intersection-observer';
import { Zap, Leaf, Shield, CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const BenefitCard = ({ icon: Icon, number, title, description, delay = 0 }) => {
  const [ref, inView] = useInView({ threshold: 0.2, triggerOnce: true });

  return (
    <div 
      ref={ref} 
      className={`group transition-all duration-1000 ${inView ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="relative h-full">
        {/* Glow Effect */}
        <div className="absolute -inset-2 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-3xl blur opacity-0 group-hover:opacity-20 transition duration-500"></div>
        
        {/* Card Content */}
        <div className="relative bg-white rounded-2xl p-8 h-full shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-105 border border-gray-100">
          {/* Benefit Number */}
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-2xl text-white font-bold text-xl mb-6">
            {number}
          </div>
          
          {/* Icon */}
          <div className="mb-6">
            <Icon className="w-12 h-12 text-blue-600 group-hover:text-cyan-500 transition-colors duration-300" />
          </div>

          {/* Title */}
          <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors duration-300">
            {title}
          </h3>

          {/* Description */}
          <p className="text-gray-600 leading-relaxed mb-6">
            {description}
          </p>

          {/* Learn More Link */}
          <div className="flex items-center text-blue-600 font-semibold group-hover:text-cyan-500 transition-colors duration-300">
            <span className="text-sm">Learn More</span>
            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </div>
        </div>
      </div>
    </div>
  );
};

export const NewBenefitsSection = () => {
  const benefits = [
    {
      number: "01",
      icon: Zap,
      title: "Incredibly Simple",
      description: "Our unique and patented product, NarcX™, is the ONLY liquid solution that immediately renders pills, tablets, capsules, and liquids non-divertible and ultimately non-retrievable—and the only on-site method of medication destruction that is CFR compliant."
    },
    {
      number: "02", 
      icon: Leaf,
      title: "Eco-Friendly Liquid",
      description: "Our products are environmentally safe, meaning you can toss depleted solutions right into the trash. Pills, tablets, capsules, and liquids begin to dissolve immediately and become non-retrievable in minutes to hours."
    },
    {
      number: "03",
      icon: Shield,
      title: "Safe And Secure",
      description: "Compounds are rendered non-retrievable on site, eliminating the need for incineration. Additionally, medications are indiscernible and indigestible in the solution to discourage tampering, keeping both controlled and non controlled substances out of the wrong hands."
    }
  ];

  const [headerRef, headerInView] = useInView({ threshold: 0.3, triggerOnce: true });

  return (
    <section className="py-24 bg-gradient-to-br from-gray-50 via-white to-blue-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 -left-64 w-128 h-128 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 -right-64 w-128 h-128 bg-cyan-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div 
          ref={headerRef}
          className={`text-center mb-20 transition-all duration-1000 ${headerInView ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
        >
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 text-blue-800 text-sm font-medium mb-6">
            <CheckCircle2 className="w-4 h-4 mr-2" />
            What Makes NarcX™ The Best Solution?
          </div>
          
          <h2 className="text-4xl md:text-6xl font-black text-gray-900 mb-6">
            Three <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Core Benefits</span>
          </h2>
          
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-8">
            Created by a retired Federal Special Agent who oversaw diversion. Our patented environmentally-safe NarcX™ solution is the first and only method of on-site destruction that is independently-validated and in strict accordance with Code of Federal Regulations 21 CFR 1300.05 policy and guidelines.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20">
          {benefits.map((benefit, index) => (
            <BenefitCard
              key={index}
              icon={benefit.icon}
              number={benefit.number}
              title={benefit.title}
              description={benefit.description}
              delay={index * 200}
            />
          ))}
        </div>

        {/* Bottom CTA Section */}
        <div className="text-center bg-gradient-to-r from-blue-600 to-cyan-500 rounded-3xl p-12 text-white">
          <h3 className="text-3xl md:text-4xl font-bold mb-4">
            NarcX™ Eliminates Incineration & Drug Diversion
          </h3>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Our patented environmentally-safe NarcX™ solution is independently-validated and exceeds federal compliance standards.
          </p>
          <Button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-3 rounded-xl font-semibold text-lg transition-all duration-300 hover:scale-105">
            Watch Demonstration Video
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </div>
    </section>
  );
};